import MojitoCore from 'mojito/core';
import { actions as bootstrapActions } from './bootstrap/slice.js';
import eventGroupsProvider from './eventgroups/provider';
import sportMetaInformationProvider from './sports-content/content-provider/sport-meta-information-provider';
import eventProvider from './sports-content/content-provider/event-provider';
import eventTipsProvider from './sports-content/content-provider/event-tips-provider';
import eventStreamsSpecProvider from './sports-content/content-provider/event-streams-spec-provider';
import itemListProvider from './item-list/provider';
import containerProvider from './sports-content/content-provider/container-provider';
import marketGroupsProvider from './sports-content/content-provider/market-groups-provider';
import marketProvider from './sports-content/content-provider/market-provider';
import tournamentsProvider from './sports-content/content-provider/tournament-provider';
import menusProvider from './sports-content/content-provider/menus-provider';
import metaInformationProvider from './meta-information/provider';
import matchAccaServiceFactory from './match-acca/service/service-factory.js';
import meetingProvider from './sports-content/content-provider/meeting-provider';
import externalNavigationServiceFactory from './external-navigation/service/service-factory';
import pdfService from './pdf/pdf-service';
import promotionsProvider from './promotions/provider';
import raceRegionProvider from './sports-content/content-provider/race-region-provider';
import videoStreamingServiceFactory from './video-streaming/service/service-factory.js';
import AbstractMessagingService from './messaging/service/abstract-messaging-service';
import bonusService from './bonus/service/bonus-service';
import bonusServiceFactory from './bonus/service/service-factory';
import sharingServiceFactory from './sharing/service/service-factory';
import { actions as userSettingsActions } from './user-settings/slice.js';
import { actions as notificationEventActions } from './sports-content/notification-event/slice.js';
import systemInformationProvider from './system-information/provider';
import analyticsreporter from './analytics/analyticsreporter';
import { actions as authenticationActions } from './authentication/slice.js';
import { actions as appBannerActions } from './app-banner/slice.js';
import { actions as favoritesActions } from './favorites/slice.js';
import quickStartGuideActions from './quick-start-guide/actions.js';
import contentChannelFactory from './common/content/content-channel-factory.js';
import { omit } from 'mojito/utils';
import { actions as searchActions } from './search/slice.js';
import messagingServiceFactory from './messaging/service/service-factory';
import promotionsServiceFactory from './promotions/service-factory.js';

const {
    actions: systemSettingsActions,
    selectors: { selectLanguage },
} = MojitoCore.Services.SystemSettings;
const { dispatch } = MojitoCore.Services.redux.store;
const { configRegistry } = MojitoCore.Services.Config;
const log = MojitoCore.logger.get('MojitoServices');

/**
 * This consists of Mojito services associated with business and application data models.
 * The services layer is partitioned into two sub-layers:
 * - The upper public layer includes various stores, implemented using the Alt flux implementation, which provide data to Mojito's higher layers.
 * - The lower internal layer fetches content data from the Mojito core layer's repository.
 *
 * To import the Mojito services module: `import MojitoServices from 'mojito/services';`.
 */

/**
 * @typedef Config
 * @type {object}
 * @property {object} configurations - Map of configurations using the configuration names as keys.
 * @property {Mojito.Services.ImplementationConfig} viewImplementations - Map of view implementations using the view names as keys.
 * @property {object} stores - Stores configuration values.
 * @property {Mojito.Services.UserSettings.Config} stores.userSettings - Initial user setting values.
 *
 * @memberof Mojito.Services
 *
 * @example <caption>Services layer config</caption>
 * init({
 *     configurations: {
 *         PageHeader: {
 *             background: '#D3D3D3',
 *             border: '1 solid transparent',
 *             borderRadius: '2px 2px 0 0',
 *             text: {
 *                 color: '#212121'
 *             }
 *         },
 *
 *         TextStyle: {
 *             fontWeight: 300
 *         },
 *
 *         l10n_sv: {
 *             strings: {
 *                 GENERIC: {
 *                     SUSPENDED: 'avbruten'
 *                 }
 *             }
 *         }
 *     },
 *
 *     viewImplementations: {
 *         ApplicationFooter: {
 *             implementation: require('views/applicationfooter/index.js').default
 *         },
 *         Button: {
 *              implementation: MyButton,
 *              suspenseLoader: MyButtonLoadingView,
 *        },
 *     },
 *
 *     stores: {
 *         content: {
 *             contentKeepAliveTime: 1000
 *         },
 *         systemSettings: {
 *             applicationMode: 'mobile',
 *             language: 'sv'
 *         },
 *         userSettings: {
 *             oddsFormat: 'decimal',
 *             timeOffset: '1'
 *         }
 *     }
 *  })
 */

/**
 * @typedef ImplementationConfig
 * @type {object}
 * @property {Function} implementation - Reference to custom react component implementation class or function.
 * @property {Function} suspenseLoader - Reference loading view class or function. Will be shown if component was imported using {@link https://reactjs.org/docs/code-splitting.html#reactlazy|React.lazy}.
 *
 * @memberof Mojito.Services
 */

class Module {
    /**
     * Initialises the services layer using the given configuration.
     *
     * @param {Mojito.Services.Config} [config = {}] - Configuration object for the Data module.
     * @function Mojito.Services#init
     */
    // Motivation to ignore sonar: don't want to refactor this function on this stage to reduce cognitive complexity.
    // No prettier - to keep no sonar on the same line
    // prettier-ignore
    async init(config = {}) {// NOSONAR
        // Set app configs
        if (config.configurations) {
            configRegistry.addOverrides(config.configurations);
        }

        // Set the viewImplementations
        if (config.viewImplementations) {
            configRegistry.addImplementations(config.viewImplementations);
        }

        // Configure Bets store
        const openBetsActions = require('./bets/open-bets/open-bets-slice').actions;
        const betsConfig = config.stores && config.stores.bets;
        dispatch(openBetsActions.configure(betsConfig));

        // Configure user info store
        const userInfoActions = require('./user-info/slice.js').actions;
        dispatch(userInfoActions.configure(config.stores && config.stores.userInfo));

        // Init search service
        if (config.searchService) {
            dispatch(
                searchActions.init({
                    service: config.searchService,
                })
            );
            // Configure search store
            searchActions.configure(config.stores && config.stores.search);
        }

        const systemSettings = config?.stores?.systemSettings || {};
        dispatch(systemSettingsActions.configure(systemSettings));

        // Init external navigation service
        if (config.externalNavigationService) {
            externalNavigationServiceFactory.init({
                service: config.externalNavigationService.service,
            });
        }

        // Configure analytics reporter
        if (config.analytics) {
            analyticsreporter.config(config.analytics);
        }

        // Init app banner settings from app config
        dispatch(appBannerActions.configure(config.stores?.appBanner || {}));

        // Init user setting from app config
        if (config.stores) {
            dispatch(userSettingsActions.setApplicationDefaults(config.stores?.userSettings || {}));
        }

        // Configure all Content stores
        const contentConfig = config.stores?.content;
        contentChannelFactory.configure(contentConfig);

        const { transactionUrl } = config;
        if (config.bootstrapService) {
            dispatch(
                bootstrapActions.configure({
                    service: config.bootstrapService,
                    serviceUrl: transactionUrl,
                })
            );
        }

        const languageCode = selectLanguage();

        const {betslipService, transactionBetslipUrl} = config;
        if (betslipService) {
            const betslipDataRetriever = require('./betslip/data-retriever.js').default;
            betslipDataRetriever.init({
                service: betslipService.service,
                serviceUrl: transactionBetslipUrl || transactionUrl,
                refreshBonusOffersInterval: betslipService.refreshBonusOffersInterval,
                refreshOveraskStatusInterval: betslipService.refreshOveraskStatusInterval,
                refreshOveraskStatusDelay: betslipService.refreshOveraskStatusDelay,
            });
        }
        const betslipActions = require('./betslip/slice.js').actions;
        dispatch(betslipActions.configure(config.stores?.betslip));

        if (config.bonusService) {
            bonusService.configure({
                serviceUrl: config.transactionBonusUrl || transactionUrl,
            });
            const userBonusesDataRetriever = require('./user-bonuses/data-retriever').default;
            userBonusesDataRetriever.init(config.bonusService);
            bonusServiceFactory.init(config.bonusService.service);
        }

        // Configure Notification service
        const notificationsServiceConfig = config.contentNotificationsService || {};
        const notificationsService = notificationsServiceConfig.service;
        if (notificationsService) {
            const serviceUrl = config.transactionNotificationsUrl || transactionUrl;
            notificationsService.configure({
                serviceUrl,
                ...omit(notificationsServiceConfig, 'service'),
            });
            const contentNotificationsActions = require('./sports-content/content-notifications/slice.js').actions;
            dispatch(
                contentNotificationsActions.init({
                    service: notificationsService,
                    daysUntilDeletion: notificationsServiceConfig.daysUntilDeletion,
                    daysUntilDeletionForOutrights:
                        notificationsServiceConfig.daysUntilDeletionForOutrights,
                })
            );
        }

        // Configure Bets service and data retriever
        if (config.betsService) {
            const betHistoryDataRetriever = require('./bets/bet-history/bet-history-data-retriever').default;
            const openBetsDataRetriever = require('./bets/open-bets/open-bets-data-retriever').default;
            const settledBetsDataRetriever = require('./bets/settled-bets/settled-bets-data-retriever').default;
            const betsServiceFactory = require('./bets/service-factory').default;
            const { service, openBets, settledBets, cashout = {} } = config.betsService;
            const cashoutConfig = { serviceUrl: config.transactionCashoutUrl || transactionUrl, ...cashout };
            const serviceSettings = {
                service: service,
                serviceUrl: config.transactionBetsUrl || transactionUrl,
            }

            betsServiceFactory.init({
                ...serviceSettings,
                cashoutConfig
            });
            betHistoryDataRetriever.init(serviceSettings);
            openBetsDataRetriever.init({
                ...serviceSettings,
                openBetsConfig: openBets,
            })
            settledBetsDataRetriever.init({
                ...serviceSettings,
                settledBetsConfig: settledBets,
            });
        }

        // Init match acca data retriever
        if (config.matchAccaService) {
            const matchAccaUrl = config.transactionMatchAccaUrl || transactionUrl;
            matchAccaServiceFactory.init({service: config.matchAccaService.service, serviceUrl: matchAccaUrl})
        }

        // Init authentication data retriever
        if (config.authenticationService) {
            dispatch(
                authenticationActions.init({
                    service: config.authenticationService.service,
                    transactionUrl: config.transactionAuthenticationUrl || transactionUrl,
                    languageCode,
                    imsConfig: config.ims,
                    ssoServiceImpl: config.authenticationService.ssoImplementation,
                })
            );
        }

        // Init messaging data retriever
        const messagingServiceInstance = config.messagingService?.service;
        if (messagingServiceInstance) {
            if (messagingServiceInstance instanceof AbstractMessagingService) {
                messagingServiceFactory.init(messagingServiceInstance)
            } else {
                log.warn(
                    `Check 'messagingService.service' config value. Provided instance should implement Mojito.Services.Messaging.AbstractMessagingService`
                );
            }
        }

        // Init user info data retriever
        if (config.userInfoService) {
            const userInfoDataRetriever = require('./user-info/data-retriever').default;
            userInfoDataRetriever.init({
                service: config.userInfoService,
                serviceUrl: config.transactionUserInfoUrl || transactionUrl,
            });
        }

        if (config.quickStartGuideService) {
            dispatch(
                quickStartGuideActions.init({
                    service: config.quickStartGuideService.service,
                })
            );
        }
        if (config.favoritesService) {
            dispatch(
                favoritesActions.init({
                    service: config.favoritesService.service,
                })
            );
        }

        if (config.sharingService) {
            sharingServiceFactory.init({ service: config.sharingService.service });
        }

        if (config.pdfServiceUrl) {
            pdfService.init(config.pdfServiceUrl);
        }

        // Init video streaming data retriever
        const { transactionVideoStreamingUrl } = config;
        videoStreamingServiceFactory.init({
            service: config?.videoStreamingService?.service,
            serviceUrl: transactionVideoStreamingUrl || transactionUrl,
        });
        if (config.promotionsService) {
            const {service, authService} = config.promotionsService;
            const serviceUrl = config.transactionPromotionUrl || transactionUrl;
            promotionsServiceFactory.init({ service, authService, serviceUrl });
        }

        if (config.notificationEventResolverService) {
            dispatch(
                notificationEventActions.init({
                    service: config.notificationEventResolverService,
                    serviceUrl: config.notificationEventResolverUrl || transactionUrl,
                })
            );
        }

        // Start data providers
        systemInformationProvider.init();
        eventProvider.init(languageCode);
        eventTipsProvider.init(languageCode);
        eventStreamsSpecProvider.init();
        eventGroupsProvider.init(languageCode);
        marketGroupsProvider.init(languageCode);
        marketProvider.init(languageCode);
        tournamentsProvider.init(languageCode);
        meetingProvider.init(languageCode);
        raceRegionProvider.init();
        containerProvider.init(languageCode);
        itemListProvider.init();
        promotionsProvider.init(languageCode);
        metaInformationProvider.init(languageCode);
        sportMetaInformationProvider.init(languageCode);
        menusProvider.init(languageCode);

        dispatch(bootstrapActions.init());
    }

    /**
     * Dispose services layer. Stops all background processing.
     *
     * @function Mojito.Services#dispose
     */
    dispose() {
        dispatch(bootstrapActions.dispose());
    }
}

export default new Module();
